import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import Box from "@mui/material/Box";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Typography from "@mui/material/Typography";
import { SURFACE as S, WEEKDAY_COLORS_BY_ISO } from "../designTokens";
import { DateRangePreset, defaultPresetGroups } from "./presets";

dayjs.locale("th");

/** How far back the calendar reaches when a page passes no `minDate`. */
export const DEFAULT_LOOKBACK_YEARS = 5;

/**
 * Rendered height of a MUI `size="small"` TextField. The trigger sits in filter rows
 * beside those inputs, so matching it is what keeps a row's controls the same size.
 */
const TRIGGER_HEIGHT = 40;

const matchPreset = (groups: DateRangePreset[][], from: Dayjs, to: Dayjs) =>
  groups
    .flat()
    .find((p) => {
      const [a, b] = p.range();
      return a.isSame(from, "day") && b.isSame(to, "day");
    })?.id ?? "custom";

/** "ศ. 14 ส.ค. 69" for a single day; "1 ก.ค. – 31 ก.ค. 2569" for a span. */
const rangeText = (from: Dayjs, to: Dayjs) => {
  if (from.isSame(to, "day")) return from.format("dd D MMM YY");
  const left = from.year() === to.year() ? from.format("D MMM") : from.format("D MMM YYYY");
  return `${left} – ${to.format("D MMM YYYY")}`;
};

/** Monday-first, matching ISO weekday numbering. */
const WEEKDAY_HEADER = ["จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส.", "อา."];

const NavButton = ({
  dir,
  onClick,
  disabled,
}: {
  dir: "prev" | "next";
  onClick: () => void;
  disabled: boolean;
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={dir === "prev" ? "เดือนก่อนหน้า" : "เดือนถัดไป"}
    sx={{
      width: 26,
      height: 26,
      display: "grid",
      placeItems: "center",
      border: "1px solid",
      borderColor: S.line,
      bgcolor: S.card,
      borderRadius: "7px",
      color: S.tx2,
      fontSize: 10,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.3 : 1,
      "&:hover:not(:disabled)": { borderColor: S.accent, color: S.accent },
    }}
  >
    {dir === "prev" ? "◀" : "▶"}
  </Box>
);

interface MonthGridProps {
  month: Dayjs;
  lo: Dayjs;
  hi: Dayjs;
  today: Dayjs;
  minDate: Dayjs;
  maxDate: Dayjs;
  onPick: (d: Dayjs) => void;
  onHover: (d: Dayjs) => void;
  nav: "prev" | "next";
  onNav: () => void;
  navDisabled: boolean;
}

const MonthGrid = ({
  month,
  lo,
  hi,
  today,
  minDate,
  maxDate,
  onPick,
  onHover,
  nav,
  onNav,
  navDisabled,
}: MonthGridProps) => {
  const lead = (month.day() + 6) % 7; // Monday-first offset
  const cells: (Dayjs | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: month.daysInMonth() }, (_, i) => month.date(i + 1)),
  ];

  return (
    <Box sx={{ width: 216, flex: "0 0 216px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 26,
          mb: 1,
        }}
      >
        {nav === "prev" ? (
          <NavButton dir="prev" onClick={onNav} disabled={navDisabled} />
        ) : (
          <Box sx={{ width: 26 }} />
        )}
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: S.tx }}>
          {month.format("MMM YYYY")}
        </Typography>
        {nav === "next" ? (
          <NavButton dir="next" onClick={onNav} disabled={navDisabled} />
        ) : (
          <Box sx={{ width: 26 }} />
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {WEEKDAY_HEADER.map((w, i) => (
          <Box
            key={w}
            sx={{
              fontSize: 10,
              textAlign: "center",
              pb: 0.75,
              fontWeight: 600,
              color: WEEKDAY_COLORS_BY_ISO[i + 1],
            }}
          >
            {w}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {cells.map((d, i) => {
          if (!d) return <Box key={`blank-${i}`} sx={{ height: 29 }} />;
          const weekend = d.day() === 0 || d.day() === 6;
          const isToday = d.isSame(today, "day");
          const disabled = d.isAfter(maxDate, "day") || d.isBefore(minDate, "day");
          const isPick = d.isSame(lo, "day") || d.isSame(hi, "day");
          const inRange = d.isAfter(lo, "day") && d.isBefore(hi, "day");

          return (
            <Box
              key={d.format("YYYY-MM-DD")}
              component="button"
              type="button"
              disabled={disabled}
              aria-label={d.format("D MMMM YYYY")}
              aria-pressed={isPick}
              onClick={() => onPick(d)}
              onMouseEnter={() => onHover(d)}
              sx={{
                height: 29,
                border: 0,
                p: 0,
                position: "relative",
                font: "inherit",
                fontSize: 12.5,
                fontWeight: isPick ? 600 : 400,
                cursor: disabled ? "not-allowed" : "pointer",
                color: isPick ? "#fff" : disabled ? S.txDisabled : weekend ? S.tx3 : S.tx,
                borderRadius: inRange ? 0 : "7px",
                bgcolor: isPick ? S.accent : inRange ? S.accentTint : "transparent",
                "&:hover:not(:disabled)": { bgcolor: isPick ? S.accent : S.accentTint },
              }}
            >
              {d.date()}
              {isToday && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: 3,
                    transform: "translateX(-50%)",
                    width: 3,
                    height: 3,
                    borderRadius: "50%",
                    bgcolor: isPick ? "#fff" : "#DC4A4A",
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export interface DateRangePickerProps {
  dateFrom: Dayjs;
  dateTo: Dayjs;
  onChange: (from: Dayjs, to: Dayjs) => void;
  /**
   * Latest selectable day. Defaults to today — pass a future date on pages that plan ahead
   * (route plans, scheduling) rather than report on the past.
   */
  maxDate?: Dayjs;
  /** Earliest selectable day. Defaults to five years back. */
  minDate?: Dayjs;
  /**
   * Shortcut rail, rendered as visually separated groups. Defaults to
   * `defaultPresetGroups()` with no "ทั้งหมดที่มี" — pass that in explicitly with the page's
   * own lookback so no page inherits another's idea of "all time".
   */
  presetGroups?: DateRangePreset[][];
  /** Greys the trigger out and keeps the calendar shut, for a page that ignores the dates for now. */
  disabled?: boolean;
}

/**
 * Two-month range picker with a shortcut rail. Click a start day then an end day (the range
 * previews on hover), pick a shortcut, or confirm a single day with "ใช้ช่วงนี้".
 */
export default function DateRangePicker({
  dateFrom,
  dateTo,
  onChange,
  maxDate,
  minDate,
  presetGroups,
  disabled = false,
}: DateRangePickerProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => dateTo.subtract(1, "month").startOf("month"));
  const [draftFrom, setDraftFrom] = useState<Dayjs>(dateFrom);
  const [draftTo, setDraftTo] = useState<Dayjs>(dateTo);
  const [awaitingEnd, setAwaitingEnd] = useState(false);
  const [hoverDate, setHoverDate] = useState<Dayjs | null>(null);

  const today = useMemo(() => dayjs().startOf("day"), []);
  const resolvedMax = maxDate ?? today;
  const resolvedMin = useMemo(
    () => minDate ?? today.subtract(DEFAULT_LOOKBACK_YEARS, "year"),
    [minDate, today],
  );
  const groups = useMemo(() => presetGroups ?? defaultPresetGroups(), [presetGroups]);

  const activePreset = matchPreset(groups, dateFrom, dateTo);
  const activeLabel =
    groups.flat().find((p) => p.id === activePreset)?.label ?? "กำหนดเอง";

  const closePopover = useCallback(() => setOpen(false), []);

  // The page can disable the range while the calendar is open — a keyboard user tabs on and
  // types into the field that takes the dates over. The calendar is shut then, so nothing
  // commits a range the page ignores, and it does not spring back open once the dates apply.
  useEffect(() => {
    if (disabled) closePopover();
  }, [disabled, closePopover]);
  const isOpen = open && !disabled;

  const openPopover = useCallback(() => {
    setDraftFrom(dateFrom);
    setDraftTo(dateTo);
    setAwaitingEnd(false);
    setHoverDate(null);
    setViewMonth(dateTo.subtract(1, "month").startOf("month"));
    setOpen(true);
  }, [dateFrom, dateTo]);

  // Escape closes from anywhere. A keydown handler on the popover itself would only fire
  // once something inside it holds focus, which is usually never.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePopover();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closePopover]);

  const commit = useCallback(
    (from: Dayjs, to: Dayjs) => {
      const [lo, hi] = from.isAfter(to) ? [to, from] : [from, to];
      onChange(lo, hi);
      closePopover();
    },
    [onChange, closePopover],
  );

  const handleDayClick = (d: Dayjs) => {
    if (!awaitingEnd) {
      setDraftFrom(d);
      setDraftTo(d);
      setAwaitingEnd(true);
      setHoverDate(d);
    } else {
      commit(draftFrom, d);
    }
  };

  const [lo, hi] = useMemo(() => {
    const end = awaitingEnd && hoverDate ? hoverDate : draftTo;
    return draftFrom.isAfter(end) ? [end, draftFrom] : [draftFrom, end];
  }, [awaitingEnd, hoverDate, draftFrom, draftTo]);

  const rightMonth = viewMonth.add(1, "month");
  const spanDays = dateTo.diff(dateFrom, "day") + 1;

  return (
    <Box>
      <Box
        component="button"
        type="button"
        ref={anchorRef}
        onClick={() => (open ? closePopover() : openPopover())}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          width: "100%",
          height: TRIGGER_HEIGHT,
          font: "inherit",
          fontSize: 13.5,
          color: S.tx,
          bgcolor: S.card,
          border: "1px solid",
          borderColor: isOpen ? S.accent : S.line,
          boxShadow: isOpen ? S.focusRing : "none",
          borderRadius: S.radiusSm,
          px: 1.5,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: (theme) => (disabled ? theme.palette.action.disabledOpacity : 1),
          "&:hover:not(:disabled)": { borderColor: S.accentBorder },
        }}
      >
        <Box
          component="span"
          sx={{
            flex: "0 0 auto",
            fontSize: 11.5,
            fontWeight: 600,
            bgcolor: S.accentTint,
            color: S.accent,
            borderRadius: "5px",
            px: 1,
            py: 0.25,
          }}
        >
          {activeLabel}
        </Box>
        <Box
          component="span"
          sx={{
            flex: 1,
            textAlign: "left",
            color: S.tx2,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {rangeText(dateFrom, dateTo)}
          {spanDays > 1 && `  (${spanDays} วัน)`}
        </Box>
        <Box component="span" sx={{ color: S.tx3, fontSize: 9 }}>
          ▼
        </Box>
      </Box>

      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        sx={{ zIndex: (t) => t.zIndex.modal }}
        modifiers={[
          { name: "offset", options: { offset: [0, 8] } },
          { name: "preventOverflow", options: { padding: 8 } },
        ]}
      >
        <ClickAwayListener onClickAway={closePopover}>
          <Paper
            role="dialog"
            aria-label="เลือกวันหรือช่วงวัน"
            elevation={0}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              p: 0,
              borderRadius: S.radius,
              border: "1px solid",
              borderColor: S.line,
              boxShadow: S.shadowPopover,
              overflow: "hidden",
            }}
          >
            {/* Shortcut rail — sized to its content so it never scrolls independently */}
            <Box
              sx={{
                flex: { xs: "0 0 auto", md: "0 0 160px" },
                bgcolor: S.head,
                borderRight: { md: `1px solid ${S.line}` },
                borderBottom: { xs: `1px solid ${S.line}`, md: 0 },
                p: 1,
                display: "flex",
                flexDirection: { xs: "row", md: "column" },
                gap: { xs: 0.5, md: 0 },
                overflowX: { xs: "auto", md: "visible" },
              }}
            >
              {groups.map((group, gi) => (
                <Box
                  key={group[0]?.id ?? gi}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "row", md: "column" },
                    gap: { xs: 0.5, md: 0 },
                    ...(gi > 0 && {
                      borderTop: { xs: 0, md: `1px solid ${S.line}` },
                      mt: { xs: 0, md: 0.75 },
                      pt: { xs: 0, md: 0.75 },
                    }),
                  }}
                >
                  {group.map((p) => {
                    const active = activePreset === p.id;
                    return (
                      <Box
                        key={p.id}
                        component="button"
                        type="button"
                        onClick={() => commit(...p.range())}
                        aria-pressed={active}
                        sx={{
                          textAlign: "left",
                          whiteSpace: "nowrap",
                          border: 0,
                          font: "inherit",
                          fontSize: 13,
                          fontWeight: active ? 600 : 400,
                          color: active ? S.accent : S.tx2,
                          bgcolor: active ? S.accentTint : "transparent",
                          borderRadius: S.radiusSm,
                          px: 1.5,
                          py: 0.85,
                          cursor: "pointer",
                          "&:hover": {
                            bgcolor: active ? S.accentTint : S.line2,
                            color: active ? S.accent : S.tx,
                          },
                        }}
                      >
                        {p.label}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>

            <Box sx={{ px: 2, pt: 1.75, pb: 1.5, flex: "0 0 auto" }}>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
                <MonthGrid
                  month={viewMonth}
                  lo={lo}
                  hi={hi}
                  today={today}
                  minDate={resolvedMin}
                  maxDate={resolvedMax}
                  onPick={handleDayClick}
                  onHover={(d) => awaitingEnd && setHoverDate(d)}
                  nav="prev"
                  onNav={() => setViewMonth((m) => m.subtract(1, "month"))}
                  navDisabled={!viewMonth.isAfter(resolvedMin, "month")}
                />
                <MonthGrid
                  month={rightMonth}
                  lo={lo}
                  hi={hi}
                  today={today}
                  minDate={resolvedMin}
                  maxDate={resolvedMax}
                  onPick={handleDayClick}
                  onHover={(d) => awaitingEnd && setHoverDate(d)}
                  nav="next"
                  onNav={() => setViewMonth((m) => m.add(1, "month"))}
                  navDisabled={!rightMonth.isBefore(resolvedMax, "month")}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  mt: 1.75,
                  pt: 1.5,
                  borderTop: `1px solid ${S.line}`,
                }}
              >
                <Box>
                  <Typography
                    sx={{ fontSize: 12.5, color: S.tx2, fontVariantNumeric: "tabular-nums" }}
                  >
                    <Box component="b" sx={{ color: S.tx, fontWeight: 700 }}>
                      {rangeText(lo, hi)}
                    </Box>
                    {!lo.isSame(hi, "day") && ` · ${hi.diff(lo, "day") + 1} วัน`}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: S.tx3 }}>
                    {awaitingEnd
                      ? "คลิกวันที่สองเพื่อกำหนดช่วง หรือกด “ใช้ช่วงนี้” เพื่อเลือกวันเดียว"
                      : "คลิกวันเริ่ม แล้วคลิกวันสิ้นสุด"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={closePopover}
                    sx={{
                      font: "inherit",
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor: S.line,
                      bgcolor: S.card,
                      color: S.tx2,
                      borderRadius: S.radiusSm,
                      px: 1.9,
                      py: 0.9,
                      "&:hover": { borderColor: S.accentBorder },
                    }}
                  >
                    ยกเลิก
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => commit(lo, hi)}
                    sx={{
                      font: "inherit",
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor: S.accent,
                      bgcolor: S.accent,
                      color: "#fff",
                      borderRadius: S.radiusSm,
                      px: 1.9,
                      py: 0.9,
                      "&:hover": { bgcolor: S.accentDark, borderColor: S.accentDark },
                    }}
                  >
                    ใช้ช่วงนี้
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
